// Must not import React: the on-screen picker and the PDF renderer share this
// model so the printed diagram matches what the doctor placed on screen.

const toHex = (n: number): string => n.toString(16).padStart(2, '0');

// Channels stay in [20, 169] so a color is never too light nor pure black; hex
// (not rgb()) so @react-pdf renders it identically to the browser.
const MIN_CHAN = 20;
const CHAN_RANGE = 150;

/** Generated once per elastic and stored on the placement, so the wizard and
 *  PDF match and the color survives reorders. */
export function randomElasticColor(): string {
    const chan = () => MIN_CHAN + Math.floor(Math.random() * CHAN_RANGE);
    return `#${toHex(chan())}${toHex(chan())}${toHex(chan())}`;
}

/** Deterministic color hashed from the placement id - used for placements
 *  persisted before colors were stored, so UI and PDF still agree. */
function fallbackColor(id: string): string {
    let h = 2166136261;
    for (let i = 0; i < id.length; i++) {
        h ^= id.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    const chan = (shift: number) => MIN_CHAN + (((h >>> shift) & 0xff) % CHAN_RANGE);
    return `#${toHex(chan(0))}${toHex(chan(8))}${toHex(chan(16))}`;
}

/** The band color for a placement: its stored color, or a stable fallback. */
export function placementColor(placement: ElasticPlacement): string {
    return placement.color && placement.color.length > 0
        ? placement.color
        : fallbackColor(placement.id);
}

/** Max teeth a single elastic band may hook. Matches the backend zod schema. */
export const MAX_ELASTIC_TEETH = 8;

/** Highest aligner number an elastic scheme may span. Matches the backend zod schema. */
export const MAX_ELASTIC_ALIGNER = 200;

/** `teeth` holds FDI numbers in placement order; the band closes back to the
 *  first tooth. */
export interface ElasticPlacement {
    id: string;
    name: string;
    teeth: number[];
    size: string;
    wear: string;
    /** FDI numbers (subset of `teeth`) whose bond button is on the lingual side;
     *  any tooth not listed defaults to buccal. */
    lingualTeeth?: number[];
    color?: string;
}

/** Which side of the tooth a bond button sits on. */
export type ButtonSide = 'buccal' | 'lingual';

/** Per-side elastics from before the odontogram, kept for events that predate it. */
export interface LegacyElastics {
    leftElastic: string;
    rightElastic: string;
    leftElasticSize: string;
    rightElasticSize: string;
}

/** Elastics worn over an inclusive aligner span (`from`..`to`); a record holds
 *  several so placement changes across treatment are captured. */
export interface ElasticScheme {
    id: string;
    name: string;
    from: number;
    to: number;
    elastics: ElasticPlacement[];
}

/** Daily wear-time options for an elastic band. Mirrors the aligner-wear
 *  select and adds the tighter hour bands requested for elastics. */
export const ELASTIC_WEAR_OPTIONS: readonly string[] = [
    'Nightly',
    'After School/Eve/Night',
    'Full Time',
    '12-14h',
    '14-16h',
    '16-18h',
    '18-20h',
    '20-22h',
    'Optional'
];

/** Wear applied to a newly placed elastic band (still editable). */
export const DEFAULT_ELASTIC_WEAR = '12-14h';

/** Buccal buttons are round, lingual ones triangular, so the side stays
 *  readable on a black & white printout. */
export type ButtonShape = 'circle' | 'triangle';

/** Drawn on every tooth an elastic lands on, so the doctor knows where to bond;
 *  shared between UI and PDF. */
export const ELASTIC_BUTTON = {
    radius: 11,
    fill: '#F26A63',
    stroke: '#B4322B',
    strokeWidth: 2.5,
    shape: 'circle'
} as const;

/** Lingual bond button — a blue triangle, to distinguish from the red buccal
 *  circle both by color and by shape (survives B&W printing). */
export const ELASTIC_BUTTON_LINGUAL = {
    radius: 11,
    fill: '#6AA9F2',
    stroke: '#2B62B4',
    strokeWidth: 2.5,
    shape: 'triangle'
} as const;

/** Style for a bond button on the given side. */
export function elasticButtonStyle(side: ButtonSide) {
    return side === 'lingual' ? ELASTIC_BUTTON_LINGUAL : ELASTIC_BUTTON;
}

/** Upward triangle centered at (cx, cy), sized to carry the same visual weight
 *  as a circle of the given radius. */
export function buttonTrianglePath(cx: number, cy: number, radius: number): string {
    // Circumradius slightly larger than the circle so the triangle reads at the
    // same weight despite its thinner corners.
    const r = radius * 1.35;
    const dx = (r * Math.sqrt(3)) / 2; // horizontal offset of the base vertices
    const dy = r / 2; // vertical offset of the base below center
    const f = (n: number) => n.toFixed(2);
    const top = `${f(cx)} ${f(cy - r)}`;
    const left = `${f(cx - dx)} ${f(cy + dy)}`;
    const right = `${f(cx + dx)} ${f(cy + dy)}`;
    return `M ${top} L ${right} L ${left} Z`;
}

/** Union of the previous LEFT and RIGHT lists so no clinical option is lost
 *  now that size is captured per elastic. */
export const ELASTIC_SIZE_OPTIONS: readonly string[] = [
    '3/16 4-4.5oz Med',
    '3/16 6-6.5oz Heavy',
    '5/16 4-4.5oz Med',
    '5/16 6-6.5oz Heavy',
    '1/8" 6-6.5 oz',
    '1/8" 6.5 oz',
    '3/16 8oz',
    '5/16 8oz'
];

/** Size applied to a newly placed elastic (still editable / required). */
export const DEFAULT_ELASTIC_SIZE = '3/16 4-4.5oz Med';
